import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import RightWrap from "../rightside/right_wrap";
import HeaderMenu from "../sidebar/headermenu";
import { API_STATUS } from "../../constants";
import { CreatePostApi } from "../../apis/posts/create_post_api";
import { PostNoteAllApi } from "../../apis/notification/post_note_all";
import FacebookLoginButton from "./facebook_login";
import SelectPlatform from "./select_platform";
import FacebookMedia from "./facebook_media";
import InstagramMedia from "./instagram_media";

export default function CreatePost() {
    const params = useParams({ insta_auth_success: String });
    const navigate = useNavigate();

    // Track platform state and modal status
    const [selectedPlatform, setSelectedPlatform] = useState(params.insta_auth_success ? { platform: 'instagram', type: 'video' } : null); // { platform: 'facebook', type: 'video' }
    const [isModalOpen, setIsModalOpen] = useState(params.insta_auth_success ? false : true);
    const [postApi, setPostApi] = useState(API_STATUS);
    const [selectedMedia, setSelectedMedia] = useState([]);

    // Workflow state: Step 1 (Select Media), Step 2 (Write Content & Publish)
    const [currentStep, setCurrentStep] = useState(1);
    const [postTitle, setPostTitle] = useState("");
    const [postCaption, setPostCaption] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (sessionStorage.getItem("LIVENESS") === '0') {
            navigate("/video_kyc_pending");
        }
    }, []);

    const handleSelectPlatform = (platform, type) => {
        setSelectedPlatform({ platform, type });
        setIsModalOpen(false);
        // Reset states on platform change
        setSelectedMedia([]);
        setCurrentStep(1);
        setPostTitle("");
        setPostCaption("");
        setErrorMessage("");
        setPostApi(API_STATUS);
    };

    const handleResetPlatform = () => {
        setSelectedPlatform(null);
        setIsModalOpen(true);
        setSelectedMedia([]);
        setCurrentStep(1);
        setPostTitle("");
        setPostCaption("");
        setErrorMessage("");
        setPostApi(API_STATUS);
    };

    const handleNextStep = () => {
        if (selectedMedia.length > 0) {
            setCurrentStep(2);
        }
    };

    const handleBackStep = () => {
        setCurrentStep(1);
        setErrorMessage("");
    };

    const handlePublish = () => {
        setErrorMessage(""); // Clear previous error state on retry

        const payload = {
            platform: selectedPlatform.platform,
            mediaType: selectedPlatform.type,
            caption: postCaption,
            items: selectedMedia,
        };

        CreatePostApi(payload, setPostApi);
    };

    // Helper to get image/video thumbnail source
    const getMediaSrc = (item) => {
        // 1. Instagram Media
        if (item.media_url || item.thumbnail_url) {
            return item.thumbnail_url || item.media_url || "";
        }

        // 2. Custom/DB normalized objects (if applicable)
        if (item.file_path || item.thumbnail) {
            return item.file_path || item.thumbnail || "";
        }

        // 3. Facebook Media Fallback
        if (item.source && selectedPlatform?.type !== "videos") return item.source;
        return item.picture || item.source || (item.format && item.format[0]?.picture) || "";
    };

    // Listen for API response status
    useEffect(() => {
        if (!postApi.isLoader) {
            if (postApi.isError) {
                setErrorMessage("Publish failed. Network error or server issue.");
            } else if (postApi.data != null) {
                if (postApi.data.status === 1) {
                    navigate("/posts");
                } else {
                    setErrorMessage(postApi.data.message || "Publish failed. Please try again.");
                }
            }
        }
    }, [postApi, navigate]);

    return (
        <div id="wrapper">
            <HeaderMenu />

            {/* Selection Modal */}
            <SelectPlatform
                isOpen={isModalOpen}
                onSelect={handleSelectPlatform}
            />

            <main className="2xl:ml-[--w-side] xl:ml-[--w-side-md] md:ml-[--w-side-small]">
                <div className="main__inner p-6">
                    {/* Header bar displaying active selection & Reset button */}
                    {selectedPlatform && (
                        <div className="flex items-center justify-between p-4 mb-6 rounded-lg bg-blue-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700">
                            <div>
                                <h3 className="text-lg font-semibold capitalize text-slate-800 dark:text-white">
                                    {selectedPlatform.platform} — {selectedPlatform.type}s
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Step {currentStep} of 2: {currentStep === 1 ? "Select Media" : "Compose & Publish"}
                                </p>
                            </div>

                            <button
                                onClick={handleResetPlatform}
                                className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 hover:bg-red-600 hover:text-white rounded-md transition-colors"
                            >
                                Change Platform
                            </button>
                        </div>
                    )}

                    {/* Form content triggers once platform is chosen */}
                    {selectedPlatform ? (
                        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">

                            {/* STEP 1: Select Media */}
                            {currentStep === 1 && (
                                <div>
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-xl font-bold mb-4 text-slate-800 dark:text-white">
                                            Create Post
                                        </h2>

                                        <button
                                            onClick={handleNextStep}
                                            disabled={selectedMedia.length === 0}
                                            className={`px-6 py-1.5 text-white font-medium rounded-lg shadow-sm transition-all disabled:cursor-not-allowed flex items-center gap-2 ${selectedMedia.length === 0
                                                ? 'bg-gray-300'
                                                : 'bg-blue-600 hover:bg-blue-700'
                                                }`}
                                        >
                                            <span>Next</span>
                                            <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>

                                    {selectedPlatform.platform === "facebook" && (
                                        <div className="mb-6">
                                            <FacebookMedia
                                                mediaType={selectedPlatform.type === "videos" ? "videos" : "photos"}
                                                onSelectMedia={setSelectedMedia}
                                            />
                                        </div>
                                    )}

                                    {selectedPlatform.platform === "instagram" && (
                                        <div className="mb-6">
                                            <InstagramMedia
                                                onSelectMedia={setSelectedMedia}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* STEP 2: Write Title / Thoughts & Publish */}
                            {currentStep === 2 && (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                                            Create Post
                                        </h2>
                                        <button
                                            onClick={handleBackStep}
                                            className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex items-center gap-1 transition-colors"
                                        >
                                            <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                            </svg>
                                            Back to Media Selection
                                        </button>
                                    </div>

                                    {/* Error Banner when publish fails */}
                                    {errorMessage && (
                                        <div className="flex items-center justify-between p-4 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 text-red-700 dark:text-red-300">
                                            <div className="flex items-center gap-2">
                                                <svg className="w-5 h-5 fill-current flex-shrink-0 text-red-600 dark:text-red-400" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-sm font-medium">{errorMessage}</span>
                                            </div>
                                            <button
                                                onClick={handlePublish}
                                                disabled={postApi.isLoader}
                                                className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded shadow-sm transition-colors disabled:opacity-50"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    )}

                                    {/* Selected Media Thumbnails Preview */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                                            Selected Media ({selectedMedia.length})
                                        </label>
                                        <div className="flex flex-wrap gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
                                            {selectedMedia.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="relative w-20 h-20 rounded-md overflow-hidden border border-slate-300 dark:border-slate-600 bg-slate-800"
                                                >
                                                    <img
                                                        src={getMediaSrc(item)}
                                                        alt="Selected Media"
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {selectedPlatform.type === "videos" && (
                                                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                            <svg className="w-6 h-6 fill-white" viewBox="0 0 24 24">
                                                                <path d="M8 5v14l11-7z" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Post Thoughts / Caption Field */}
                                    <div>
                                        <textarea
                                            rows={4}
                                            value={postCaption}
                                            onChange={(e) => setPostCaption(e.target.value)}
                                            placeholder="Write your thoughts or caption for this post..."
                                            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                        />
                                    </div>

                                    {/* Action Bar (Publish Button) */}
                                    <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
                                        <button
                                            onClick={handlePublish}
                                            disabled={postApi.isLoader}
                                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-all flex items-center gap-2 disabled:opacity-60"
                                        >
                                            {postApi.isLoader ? (
                                                <>
                                                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                                                    Publishing...
                                                </>
                                            ) : (
                                                "Publish Post"
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}

                        </div>
                    ) : (
                        <div className="text-center py-12 text-slate-400">
                            Please select a platform from the modal to continue creating a post.
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}